import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  type AgentProfileResponseDto,
  type CampaignListItemDto,
} from '@shared/services/apiService';
import { useAppSelector } from '@shared/store';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { CampaignPicker } from './CampaignPicker';
import { workspaceAgentPath } from './workspacePaths';

export const CreateAssistantDialog = ({
  open,
  onClose,
  job,
}: {
  open: boolean;
  onClose: () => void;
  job?: AgentProfileResponseDto;
}) => {
  const [campaign, setCampaign] = useState<CampaignListItemDto | null>(null);
  const viewer = useAppSelector((state) => state.auth.authenticatedUser?.role === 'viewer');
  const workspace = useDesignWorkspace();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="assistant-campaign-title">
      <DialogTitle id="assistant-campaign-title">Create an assistant</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          {job && <Typography variant="subtitle1">Start from {job.name}</Typography>}
          <Typography>
            Choose an inactive campaign, then configure the job, voice and provider settings and
            review your assistant before saving.
          </Typography>
          <CampaignPicker value={campaign} onChange={setCampaign} />
          <Alert severity="info">
            A campaign with an existing single agent needs a squad before another agent can be
            added.
          </Alert>
          <Button
            disabled={viewer}
            onClick={() => {
              if (viewer) return;
              onClose();
              workspace.navigate(
                `/workspace/campaign/new?${new URLSearchParams({ setup: crypto.randomUUID(), ...(job ? { job: job.id } : {}) })}`,
              );
            }}>
            Create a new campaign
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={viewer || !campaign}
          onClick={() => {
            if (!campaign || viewer) return;
            onClose();
            workspace.navigate(
              workspaceAgentPath(campaign.id, 'new', job?.id, crypto.randomUUID()),
            );
          }}>
          Continue to setup
        </Button>
      </DialogActions>
    </Dialog>
  );
};
